<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useTemplateRef } from 'vue';
import { useEventListener } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { formatPhoneNumber, getDefaultDateFormat } from '@Helpers';
import { useSessionStore } from '@Stores';
// NOTE :: 2FA SMS send/resend disabled for now — an admin-triggered SMS code has no
// actionable link for the user to follow, so the action leads nowhere. Phone status
// display is retained. Re-enable when the admin SMS flow is defined (TASK-2123).
// import { useAToast } from '@Atoms';
// import { ResendStaffPhoneVerificationService, SendStaffPhoneVerificationService } from '@StaffModule';

const emits = defineEmits<{
  onRefreshData: [];
}>();

const props = defineProps<{
  field: string;
  rowData: any;
}>();

const { shelterProfile, userProfile } = storeToRefs(useSessionStore());

// const { notify, TOAST_TYPES } = useAToast();

const popoverId = `phone-popover-${Math.random().toString(36).slice(2)}`;

const isPopoverOpen = ref(false);
const loading = ref(false);
const panel = ref();
const triggerRef = useTemplateRef<any>('triggerRef');

const sentAt = computed(() => props.rowData?.phone_verification_sent_at || null);
const verifiedAt = computed(() => props.rowData?.phone_verified_at || null);
const maskedPhone = computed(
  () => formatPhoneNumber(String(props.field || '').trim(), props.rowData?.phone_national) || props.field || '-'
);

const iconClass = computed(() => {
  // prettier-ignore
  return verificationStatus.value === 'verified' ? 'pi pi-check-circle text-green-600'
       : verificationStatus.value === 'sent' ? 'pi pi-send text-sky-600'
       : 'pi pi-clock text-amber-500';
});

const smsChannelEnabled = computed(() =>
  ['sms', 'both'].includes(shelterProfile.value?.two_factor_channels || 'email')
);

const showPhoneVerificationUi = computed(
  () =>
    !!shelterProfile.value?.shelter_two_factor_enabled && smsChannelEnabled.value && userProfile.value?.role === 'admin'
);

// NOTE :: SMS send/resend disabled — see import note above.
// const resendButtonLabel = computed(() =>
//   loading.value
//     ? 'shelterInfo.staffProfile.phoneVerification.actions.resending'
//     : 'shelterInfo.staffProfile.phoneVerification.actions.resend'
// );

const statusLabel = computed(() => {
  // prettier-ignore
  return verificationStatus.value === 'verified' ? 'shelterInfo.staffProfile.phoneVerification.status.verified'
       : verificationStatus.value === 'sent' ? 'shelterInfo.staffProfile.phoneVerification.status.sent'
       : 'shelterInfo.staffProfile.phoneVerification.status.pending';
});

const verificationStatus = computed(() => {
  const status = String(props.rowData?.phone_verification_status || '').trim();
  if (status) return status;

  if (verifiedAt.value) return 'verified';

  if (sentAt.value) return 'sent';

  return 'pending';
});

useEventListener(document, 'click', onDocumentClick);
useEventListener(window, 'af-staff-verification-popover-opened', onStaffVerificationPopoverOpened);

onBeforeUnmount(onComponentBeforeUnmount);

// HELPERS
function asDate(dateValue: any) {
  if (!dateValue) return '-';

  const formatted = getDefaultDateFormat(dateValue, true);

  return formatted || '-';
}

function getTriggerElement(): HTMLElement | null {
  const triggerElement = triggerRef.value;
  if (!triggerElement) return null;

  if (typeof triggerElement.contains === 'function') {
    return triggerElement as HTMLElement;
  }

  const rootElement = triggerElement?.$el;
  if (rootElement && typeof rootElement.contains === 'function') {
    return rootElement as HTMLElement;
  }

  return null;
}

// EVENTS
function onToggleDetails(event: Event) {
  window.dispatchEvent(
    new CustomEvent('af-staff-verification-popover-opened', {
      detail: { popoverId },
    })
  );

  panel.value?.toggle(event);
}

// NOTE :: SMS send/resend disabled — see import note above. Status display retained.
// function onSendVerification() {
//   if (loading.value) return;
//
//   loading.value = true;
//
//   SendStaffPhoneVerificationService(props.rowData._id)
//     .then(() => {
//       const TOAST_SUCCESS = {
//         title: 'shelterInfo.staffProfile.phoneVerification.toast.sent',
//         type: TOAST_TYPES.SUCCESS,
//       };
//       notify(TOAST_SUCCESS);
//
//       emits('onRefreshData');
//
//       panel.value?.hide();
//     })
//     .catch((error: any) => {
//       const TOAST_ERROR = {
//         title: error?.response?.data?.error || 'errors.generics.unknownIssue.title',
//         type: TOAST_TYPES.ERROR,
//       };
//       notify(TOAST_ERROR);
//     })
//     .finally(() => (loading.value = false));
// }
//
// function onResendVerification() {
//   if (loading.value) return;
//
//   loading.value = true;
//
//   ResendStaffPhoneVerificationService(props.rowData._id)
//     .then(() => {
//       const TOAST_SUCCESS = {
//         title: 'shelterInfo.staffProfile.phoneVerification.toast.resent',
//         type: TOAST_TYPES.SUCCESS,
//       };
//       notify(TOAST_SUCCESS);
//       emits('onRefreshData');
//       panel.value?.hide();
//     })
//     .catch((error: any) => {
//       const TOAST_ERROR = {
//         title: error?.response?.data?.error || 'errors.generics.unknownIssue.title',
//         type: TOAST_TYPES.ERROR,
//       };
//       notify(TOAST_ERROR);
//     })
//     .finally(() => (loading.value = false));
// }

function onPopoverShow() {
  isPopoverOpen.value = true;
}

function onDocumentClick(event: MouseEvent) {
  if (!isPopoverOpen.value) return;

  const target = event.target as Node | null;
  const triggerEl = getTriggerElement();
  const panelEl = panel.value?.container as HTMLElement | undefined;

  const clickedTrigger = !!(target && triggerEl && triggerEl.contains(target));
  const clickedPanel = !!(target && panelEl?.contains(target));
  if (clickedTrigger || clickedPanel) return;

  panel.value?.hide();
}

function onPopoverHide() {
  isPopoverOpen.value = false;
}

function onStaffVerificationPopoverOpened(event: Event) {
  const customEvent = event as CustomEvent<{ popoverId?: string }>;
  const openedPopoverId = customEvent?.detail?.popoverId;
  if (!openedPopoverId || openedPopoverId === popoverId) return;

  if (!isPopoverOpen.value) return;

  panel.value?.hide();
}

function onComponentBeforeUnmount() {
  panel.value?.hide();
}
</script>

<template>
  <div class="flex items-center gap-2">
    <Button
      v-if="showPhoneVerificationUi"
      rounded
      text
      class="h-6 w-6"
      ref="triggerRef"
      :icon="iconClass"
      :loading
      @click.stop="onToggleDetails"
    />

    <span class="truncate">
      {{ maskedPhone }}
    </span>

    <OverlayPanel
      v-if="showPhoneVerificationUi"
      ref="panel"
      @hide="onPopoverHide"
      @show="onPopoverShow"
    >
      <div class="min-w-64 space-y-2 text-sm">
        <p class="font-semibold">
          {{ $t(statusLabel) }}
        </p>

        <p class="max-w-[260px] text-xs text-gray-500">
          {{ $t('shelterInfo.staffProfile.phoneVerification.description') }}
        </p>

        <p v-if="verificationStatus === 'sent'">
          {{ $t('shelterInfo.staffProfile.phoneVerification.lastSent') }}: {{ asDate(sentAt) }}
        </p>

        <p
          v-if="verificationStatus === 'verified'"
          class="text-sm text-gray-500"
        >
          {{ $t('shelterInfo.staffProfile.phoneVerification.verifiedAt') }}: {{ asDate(verifiedAt) }}
        </p>

        <!--
          NOTE :: 2FA SMS send/resend disabled for now — an admin-triggered SMS code has no
          actionable link for the user to follow. Phone status (above) is retained.
          Re-enable when the admin SMS flow is defined (TASK-2123).

        <Button
          v-if="verificationStatus === 'pending'"
          class="af-button af-button-success w-fit text-sm"
          :loading
          :disabled="loading"
          :label="$t('shelterInfo.staffProfile.phoneVerification.actions.send')"
          @click="onSendVerification"
        />

        <Button
          v-if="verificationStatus === 'sent'"
          class="af-button af-button-success w-fit text-sm"
          :loading
          :disabled="loading"
          :label="$t(resendButtonLabel)"
          @click="onResendVerification"
        />
        -->
      </div>
    </OverlayPanel>
  </div>
</template>
